/** 全局应用状态 Context（含真实 RBAC 认证） */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { AppContextType, UserInfo } from '@bluplanet/shared';
import { UserRole } from '@bluplanet/shared';
import type { MenuNode } from '@bluplanet/shared';
import { userApi } from '@/api/user';
import { TOKEN_KEY } from '@/api/request';

export const defaultUser: UserInfo = {
  id: 'USR001',
  name: '张伟',
  avatar: '',
  role: UserRole.INTERNAL_OPS,
  permissions: ['*:*'],
};

const defaultAppContextValue: AppContextType = {
  user: defaultUser,
  themeMode: 'light',
  sidebarCollapsed: false,
  setUser: () => {},
  setThemeMode: () => {},
  toggleSidebar: () => {},
  logout: () => {},
  token: null,
  permissions: [],
  roles: [],
  menus: [],
  login: async () => {},
  fetchUserInfo: async () => {},
};

const AppContext = createContext<AppContextType>(defaultAppContextValue);

export { defaultAppContextValue };

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserInfo>(defaultUser);
  const [themeMode, setThemeMode] = useState<string>('light');
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [menus, setMenus] = useState<MenuNode[]>([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  /** 拉取当前用户信息并填充 menus/permissions/roles */
  const fetchUserInfo = useCallback(async () => {
    const info = await userApi.getInfo();
    const backendUser = info.user ?? ({} as UserInfo);
    const userId = backendUser.id ?? defaultUser.id;
    setUser({
      id: userId,
      name: backendUser.realName || backendUser.username || defaultUser.name,
      avatar: backendUser.avatar || '',
      role: defaultUser.role,
      permissions: info.permissions ?? [],
      username: backendUser.username,
      roles: info.roles,
      orgId: backendUser.orgId,
      isPlatformAdmin: info.isSuperAdmin ?? info.platform ?? false,
    });
    setPermissions(info.permissions ?? []);
    setRoles(info.roles ?? []);
    setMenus(info.menus ?? []);
    setIsPlatformAdmin(info.isSuperAdmin ?? info.platform ?? false);
    // 保存用户 ID 到 localStorage（供文件上传等接口使用）
    localStorage.setItem('bluplanet_user_id', userId);
  }, []);

  /** 登录：调真实接口，存 token，再拉用户信息 */
  const login = useCallback(
    async (username: string, password: string) => {
      const result = await userApi.login({ username, password });
      localStorage.setItem(TOKEN_KEY, result.token);
      setToken(result.token);
      await fetchUserInfo();
    },
    [fetchUserInfo]
  );

  /** 退出登录：清 token + 重置状态 */
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('bluplanet_user_id');
    setToken(null);
    setUser(defaultUser);
    setPermissions([]);
    setRoles([]);
    setMenus([]);
    setIsPlatformAdmin(false);
  }, []);

  /** 挂载时若已有 token，自动拉取用户信息（刷新页面保活） */
  useEffect(() => {
    if (token) {
      fetchUserInfo().catch(() => {
        // token 失效则清除
        logout();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AppContextType = {
    user,
    themeMode,
    sidebarCollapsed,
    setUser,
    setThemeMode,
    toggleSidebar,
    logout,
    token,
    permissions,
    roles,
    menus,
    isPlatformAdmin,
    login,
    fetchUserInfo,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextType {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
}

export default AppContext;
